import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";
import { Loader } from "@common/handleErrorAndLoading/Loader";
import { Modal } from "@common/Modal/Modal";
import { ICustomService } from "@common/SelectYourNumber/interface/ISubscription";
import UploadFileButton from "@common/uploadFileButton/UploadFileButton";
import { TITLE_INHERIT_STYLE } from "@constants";
import { useManageMobileServices } from "@features/ManageMobileServices/hook/useManageMobileServices";
import { Card, CardHeader, CardContent, Typography, Grid, Button } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface IParams {
    selectedService: ICustomService;
}

const ManageNuisanceCall = (props: IParams) => {
    const { selectedService } = props;
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const {
        getNuisanceCallStatusState,
        nuisanceCallStatus,
        getNuisanceCallStatus,
        formMethods,
        postNuisanceCallDocumentStatus,
        handleOnSubmit,
    } = useManageMobileServices(() => {
        setIsModalOpen(true);
    });
    const { t } = useTranslation(["accountDetails", "glossary", "common"]);
    const { errors, control } = formMethods;

    useEffect(() => {
        getNuisanceCallStatus();
    }, [selectedService]);

    return (
        <>
            <Card elevation={2}>
                <CardHeader
                    className="card-header card-header__right"
                    title={t("accountDetails:requestForNuisanceCall")}
                    titleTypographyProps={TITLE_INHERIT_STYLE}
                />
                <CardContent>
                    <div className="card-header__circle" />
                    <HandleErrorAndLoading
                        error={getNuisanceCallStatusState.error}
                        isLoading={getNuisanceCallStatusState.isLoading}
                        retryFetch={getNuisanceCallStatus}
                    >
                        {nuisanceCallStatus && (
                            <>
                                {nuisanceCallStatus.status === "none" && (
                                    <Typography align="center" variant="body1" style={{ marginBottom: "15px" }}>
                                        {t("accountDetails:nuisanceCallNone")}
                                    </Typography>
                                )}
                                {nuisanceCallStatus.status === "In progress" && (
                                    <Typography align="center" variant="body1" style={{ marginBottom: "15px" }}>
                                        {t("accountDetails:nuisanceCallInProgress")}
                                    </Typography>
                                )}
                                {nuisanceCallStatus.status === "Verified" && (
                                    <>
                                        <Typography align="center" variant="body1" style={{ marginBottom: "15px" }}>
                                            {t("accountDetails:nuisanceCallVerified1")}
                                        </Typography>
                                        <Typography align="center" variant="body1" style={{ marginBottom: "15px" }}>
                                            {t("accountDetails:nuisanceCallVerified2")}
                                        </Typography>
                                        <Typography align="center" variant="body1" style={{ marginBottom: "15px" }}>
                                            {t("accountDetails:nuisanceCallVerified3")}
                                        </Typography>
                                    </>
                                )}
                                {nuisanceCallStatus.status === "none" ? (
                                    <>
                                        <Typography align="center" variant="body1" style={{ marginBottom: "15px" }}>
                                            {t("accountDetails:fivePerRequest")}
                                        </Typography>
                                        <form onSubmit={handleOnSubmit}>
                                            <Grid container justify="center" alignItems="center" spacing={4}>
                                                <Grid
                                                    item
                                                    container
                                                    direction="column"
                                                    justify="center"
                                                    alignItems="center"
                                                    spacing={1}
                                                >
                                                    <Grid item>
                                                        <UploadFileButton
                                                            error={errors.base64File}
                                                            control={control}
                                                            name={"base64File"}
                                                            buttonText={t("accountDetails:policeDocumentUpload")}
                                                            disabled={postNuisanceCallDocumentStatus.isLoading}
                                                        />
                                                    </Grid>
                                                    {postNuisanceCallDocumentStatus.error && (
                                                        <Grid item>
                                                            <Typography color="error" variant="body1">
                                                                {postNuisanceCallDocumentStatus.error.errorMessage}
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                                <Grid
                                                    item
                                                    container
                                                    spacing={2}
                                                    direction="row"
                                                    sm={8}
                                                    xs={12}
                                                    justify="center"
                                                    alignItems="center"
                                                >
                                                    {postNuisanceCallDocumentStatus.isLoading && <Loader size={40} />}
                                                    {!postNuisanceCallDocumentStatus.isLoading && (
                                                        <>
                                                            <Grid item xs={4}>
                                                                <Button
                                                                    color="secondary"
                                                                    formNoValidate
                                                                    fullWidth
                                                                    type="submit"
                                                                    variant="contained"
                                                                >
                                                                    {t("glossary:submit")}
                                                                </Button>
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </form>
                                    </>
                                ) : (
                                    <Grid container direction="column" spacing={1}>
                                        <Grid item>
                                            <Typography>
                                                {t("accountDetails:requestStatus")}
                                                {nuisanceCallStatus.status === "In progress" &&
                                                    t("accountDetails:inProgress")}
                                                {nuisanceCallStatus.status === "Verified" &&
                                                    t("accountDetails:verified")}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography>
                                                {nuisanceCallStatus.status === "In progress" &&
                                                    t("accountDetails:submittedOn")}
                                                {nuisanceCallStatus.status === "Verified" &&
                                                    t("accountDetails:monitoringForm")}
                                                {nuisanceCallStatus.date}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                )}
                            </>
                        )}
                    </HandleErrorAndLoading>
                </CardContent>
            </Card>
            <Modal
                close={() => setIsModalOpen(false)}
                confirm={() => setIsModalOpen(false)}
                confirmLabel={t("glossary:close")}
                content={t("accountDetails:documentUploadedSuccessContent")}
                isModalOpen={isModalOpen}
                title={t("accountDetails:documentUploadedSuccessTitle")}
            />
        </>
    );
};

export default ManageNuisanceCall;
